import ApiClient from '../plugins/AxiosPlugin'
import { buildSearchEngine, loadFacetOptionsActions } from '@coveo/headless'
import { buildRecommendationEngine } from '@coveo/headless/recommendation'
import {
    applyConsentToHeadlessEngine,
    consentNavigatorContextProvider,
    hasAnalyticsConsent,
    headlessAnalyticsConfig
} from '@platform/utils/coveoConsent'

const COVEO_SEARCH_EVENT = 'CoveoSearchEvent'

export default {
    namespaced: true,
    state: {
        org: '',
        token: '',
        hub: '',
        sourceType: '',
        engine: null,
        engines: {},
        results: {},
        recommendationEngine: null,
        generatedFacetData: {},
        advancedFilters: [],
        dowCultures: {},
        analyticsConsentEnabled: false
    },
    mutations: {
        tokenAndOrg: setTokenAndOrg,
        multiTokenAndOrg: setMultiTokenAndOrg,
        engine: buildEngine,
        recommendationEngine: buildContentRecommendationEngine,
        removeEngine: removeEngine,
        updateResultList: updateResultList,
        setAdvancedFilters: setAdvancedFilters,
        clearAdvancedFilters: clearAdvancedFilters,
        setDowCultures: setDowCultures,
        analyticsConsentEnabled: setAnalyticsConsentEnabled
    },
    actions: {
        fetchSearchToken,
        updateEngine,
        getEngine,
        getRecommendationEngine,
        getGeneratedFacets,
        setFacetOrderFreeze,
        updateAnalyticsConsent,
        [COVEO_SEARCH_EVENT]: { root: true, handler() {} }
    },
    getters: {
        allAdvancedFilters: getAllAdvancedFilters
    }
}

function validateToken(storedToken, params, languageCode) {
    if(!storedToken)
        return false
    const parsedToken = JSON.parse(storedToken)
    const foundToken = parsedToken?.find((item) => {
        return (item.hub === params.hub && item.customFilter === params.customFilter && item.showAll === params.showAll && item.languageCode === languageCode)
    })
    if(!foundToken)
        return false

    if(foundToken?.resultParams?.token?.indexOf('.') > 0 && foundToken?.resultParams?.token?.split('.').length >= 2){
        try {
            const tokenJson = JSON.parse(atob(foundToken.resultParams.token.split('.')[1]))
            const expiryDate = tokenJson.hasOwnProperty("exp") ? tokenJson.exp : "no-bearer"
            if(expiryDate !== "no-bearer" && Date.now() / 1000 >= expiryDate){
                return false;
            }
        } catch (e) {
            return false;
        }
    }
    return true
}

function fetchSearchToken(context, params) {
    const storedToken = localStorage.getItem('dowComCoveoToken')
    const languageCode = this.state.browser?.languageCode || 'en-us'
    const servletURI = '/' + languageCode + context.rootState.settings.search_token_servlet

    if(validateToken(storedToken, params, languageCode)) {
        const parsedToken = JSON.parse(storedToken)
        const foundToken = parsedToken.find((item) => {
            return (item.hub === params.hub && item.customFilter === params.customFilter && item.showAll === params.showAll && item.languageCode === languageCode)
        })
        if (!params.multi) {
            context.commit('tokenAndOrg', foundToken.resultParams)
        } else {
            context.commit('multiTokenAndOrg', {
                ...foundToken.resultParams,
                showAll: params.showAll
            })
        }

        return Promise.resolve({
            data: foundToken.resultParams
        })
    } else {
        return ApiClient.get(servletURI, {
            params: {
                type: params.hub,
                customFilter: params.customFilter,
                showAll: params.showAll
            }
        }).then((result) => {
            var resultParams = {
                org: result.data.org,
                token: result.data.token,
                sourceType: params.sourceType,
                hub: params.hub,
                languageCode: languageCode
            }
            const newToken = {
                ...params,
                resultParams: resultParams
            }
            let storedToken = []

            if(localStorage.getItem('dowComCoveoToken')) {
                storedToken = JSON.parse(localStorage.getItem('dowComCoveoToken'))
                const existingTokenIndex = storedToken.findIndex((item) => {
                    return (item.hub === params.hub && item.customFilter === params.customFilter && item.showAll === params.showAll && item.languageCode !== languageCode)
                })

                if (existingTokenIndex === -1) {
                    localStorage.removeItem('dowComCoveoToken')
                    storedToken.push(newToken)
                }
                else {
                    storedToken[existingTokenIndex] = newToken
                }
            }
            else {
                storedToken.push(newToken)
            }

            localStorage.setItem('dowComCoveoToken', JSON.stringify(storedToken))

            if (!params.multi) {
                context.commit('tokenAndOrg', resultParams)
            } else {
                context.commit('multiTokenAndOrg', {
                    ...resultParams,
                    showAll: params.showAll
                })
            }
            return result
        })
    }
}

function setTokenAndOrg(state, params) {
    if (params && Object.getOwnPropertyNames(params)) {
        state.org = params.org
        state.token = params.token
        state.sourceType = params.sourceType
        state.hub = params.hub
    }
}

function setMultiTokenAndOrg(state, params) {
    if (params && Object.getOwnPropertyNames(params)) {
        const existingHubEngine = state.engines[params.hub]

        if (!existingHubEngine || existingHubEngine.showAll !== params.showAll) {
            state.engines[params.hub] = {
                token: params.token,
                org: params.org,
                sourceType: params.sourceType,
                showAll: params.showAll
            }
        }
    }
}

function updateEngine(context) {
    var params = {
        org: context.state.org,
        token: context.state.token
    }
    context.commit('engine', params)
}

function removeEngine(state) {
    state.engine = null
}

function buildEngine(state, params) {
    if (!state.engine && !params.multi) {
        const search = {
            searchHub: state.hub,
            locale: this.state.browser?.languageCode || 'en-us'
        }
        const headlessEngine = buildSearchEngine({
            configuration: {
                organizationId: params.org,
                accessToken: params.token,
                search: search,
                analytics: headlessAnalyticsConfig({
                    analyticsMode: 'legacy'
                })
            },
            navigatorContextProvider: consentNavigatorContextProvider()
        })
        applyConsentToHeadlessEngine(headlessEngine)

        state.engine = headlessEngine
    } else if (params.multi) {
        if (state.engines[params.hub] && !state.engines[params.hub].engine) {
            const search = {
                searchHub: params.hub,
                locale: this.state.browser?.languageCode || 'en-us'
            }
            const headlessEngine = buildSearchEngine({
                configuration: {
                    organizationId: state.engines[params.hub].org,
                    accessToken: state.engines[params.hub].token,
                    search: search,
                    analytics: headlessAnalyticsConfig({
                        analyticsMode: 'legacy'
                    })
                },
                navigatorContextProvider: consentNavigatorContextProvider()
            })
            applyConsentToHeadlessEngine(headlessEngine)
            state.engines[params.hub].engine = headlessEngine
        }
    }
}

function getEngine({ dispatch, commit }, { hub, showAll }) {
    dispatch('updateAnalyticsConsent')

    return dispatch('fetchSearchToken', { hub, multi: true, showAll }).then(() =>
        commit('engine', { multi: true, hub })
    )
}

function setAnalyticsConsentEnabled(state, enabled) {
    state.analyticsConsentEnabled = enabled
}

function updateAnalyticsConsent({ state, commit }) {
    const hasConsent = hasAnalyticsConsent()

    commit('analyticsConsentEnabled', hasConsent)

    Object.values(state.engines).forEach((engineState) =>
        applyConsentToHeadlessEngine(engineState?.engine)
    )
    applyConsentToHeadlessEngine(state.engine)
    applyConsentToHeadlessEngine(state.recommendationEngine)
}

function updateResultList(state, { cmp, results }) {
    state.results[cmp] = results
}

function buildContentRecommendationEngine(state, params){
    const analyticsClientMiddleware = (eventName, payload) => {
        if (eventName === 'search') {
            eventName = 'CoveoSearchEvent'
        } else if (eventName === 'click') {
            eventName = 'CoveoClickEvent'
        }
        window.adobeDataLayer.push({
            event: eventName,
            coveoAnalyticsEventData: payload
        })
        return payload
    }

    if (state.engines[params.hub] && !state.engines[params.hub].engine) {
        const search = {
            searchHub: params.hub
        }
        const headlessRecommendationEngine = buildRecommendationEngine({
            configuration: {
                organizationId: state.engines[params.hub].org,
                accessToken: state.engines[params.hub].token,
                searchHub: params.hub,
                analytics: headlessAnalyticsConfig({
                    analyticsClientMiddleware
                }),
                pipeline: 'Dow.com Content Recommender'
            },
            navigatorContextProvider: consentNavigatorContextProvider()
        })
        applyConsentToHeadlessEngine(headlessRecommendationEngine)

        state.engines[params.hub].engine = headlessRecommendationEngine
    }
}

function getRecommendationEngine({dispatch, commit}, { hub }) {
    return dispatch('fetchSearchToken', { hub, multi:true }).then((r) => 
        commit('recommendationEngine', { hub })
    )
}

/**
 * Sets the freezeFacetOrder option on the Coveo Headless engine.
 * When freeze=true, facet ordering remains stable (no DNE/ML reordering).
 * When freeze=false, allows AI/DNE to reorder facets dynamically.
 * @param {Object} context - Vuex action context
 * @param {Object} params - { hub: string, freeze: boolean }
 */
function setFacetOrderFreeze({ state }, { hub, freeze }) {
    const engine = state.engines[hub]?.engine
    if (engine) {
        const { updateFacetOptions } = loadFacetOptionsActions(engine)
        engine.dispatch(updateFacetOptions({ freezeFacetOrder: freeze }))
    }
}

function getGeneratedFacets(context, params) {
    const languageCode = this.state.browser?.languageCode || 'en-us'
    const servletURI = '/' + languageCode + context.rootState.settings.generated_facets_servlet

    ApiClient.get(servletURI, {
        params: {
            locale: params.locale
        }
    })
    .then((response) => {
        if(response.data) {
            context.state.generatedFacetData = JSON.parse(response.data);
        }
    })
    .catch((error) => console.log('Error getting generated facets:', error));
}

function setAdvancedFilters(state, { filters, replaceAll = false }) {
    const urlHash = window.location.hash.slice(1);

    const processedFilters = filters.map(element => {
        if(element.type === 'slider') {
            const rangeFromHash = getRangeFromHash(element.facet, urlHash);

            if(rangeFromHash) {
                return {
                    ...element,
                    hashMin: rangeFromHash[0],
                    hashMax: rangeFromHash[1]
                };
            }
        }
        return element;
    });

    if(replaceAll) {
        state.advancedFilters = [...processedFilters];
    } else {
        state.advancedFilters = [...state.advancedFilters, ...processedFilters];
    }
}

function getRangeFromHash(facet, hash) {
    const paramName = `nf-${facet}_1`;
    const params = new URLSearchParams(hash);
    const value = params.get(paramName);

    if(value) {
        return value.split("...");
    }

    return null;
}

function clearAdvancedFilters(state) {
    state.advancedFilters = [];
}

function getAllAdvancedFilters(state) {
    return state.advancedFilters;
}

function setDowCultures(state, data) {
    state.dowCultures = data;   
}